import React from 'react';
import {
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Tooltip,
} from '@material-ui/core';
import { PartitionType } from 'const/PartitionType';
import { uniqueId } from 'lodash';
import clsx from 'clsx';
import { FaExclamationCircle, FaSquare } from 'react-icons/fa';
import { CourierChip } from '../CourierChip/CourierChip';
import { CourierType } from 'const/CourierType';
import { CourierSelection } from '../CourierSelection/CourierSelection';
import './PartitionCard.scss';
import { AreaRating } from 'const/AreaRating';
import { Link } from 'react-router-dom';

interface PartitionCardProps {
  partition: PartitionType;
  zonePartitionsCount: Record<string, number> | undefined;
  active?: boolean;
  unusedCouriers: Array<CourierType>;
  assignedCouriers: Array<CourierType>;
  color: string;
  onSelectPartition?: (partition: PartitionType) => void;
  addCourierToPartition?: (
    courier: CourierType,
    partition: PartitionType
  ) => void;
  addCourierToActivePartition?: (courier: CourierType) => void;
  removeCourierFromPartition?: (courier: CourierType) => void;
  onCourierHoverChange?: (
    couriers?: CourierType[],
    ratingType?: AreaRating
  ) => void;
}

export const PartitionCard = ({
  partition,
  zonePartitionsCount,
  unusedCouriers,
  assignedCouriers,
  onSelectPartition,
  addCourierToPartition,
  addCourierToActivePartition,
  removeCourierFromPartition,
  ...props
}: PartitionCardProps) => {
  const orderCount = partition.zones.reduce(
    (acc, zoneId) => acc + ((zonePartitionsCount || {})[zoneId] || 0),
    0
  );
  const partitionCouriers = partition.couriers?.length || 0;
  const orderPerCourier = (orderCount / partitionCouriers).toFixed(1);

  const onPartitionClicked = () => {
    onSelectPartition(partition);
  };

  const onCourierSelected = (selectedCourier: CourierType) => {
    addCourierToPartition(selectedCourier, partition);
  };

  return (
    <Card
      className={clsx('partitions-toggle', {
        selected: props.active,
      })}
      variant="outlined"
    >
      <CardContent>
        <div className="section">
          <h3 onClick={onPartitionClicked}>
            <span className="icon">
              <FaSquare color={props.color} />
            </span>
            {partition.id ? `Partition ${partition.id}` : 'New partition'}
            {partition.id && (
              <Link
                to={`/planner/routing-debugger/${partition.id}`}
                target="_blank"
                style={{
                  fontSize: 13,
                }}
              >
                {'  '}
                See solution
              </Link>
            )}
            {props.active ? (
              <Chip label="Active" color="primary" size="small" />
            ) : (
              <Chip
                label="Select"
                variant="outlined"
                size="small"
                onClick={onPartitionClicked}
              />
            )}
          </h3>
          <Divider />
          {partition.description && <small>{partition.description}</small>}
        </div>
        <div className="section">
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>Orders: {orderCount}</Grid>
            <Grid item>Couriers: {partitionCouriers}</Grid>
            <Grid item>Orders per courier: {orderPerCourier}</Grid>
          </Grid>
        </div>

        <div className="section">
          <CourierSelection
            options={unusedCouriers}
            onSelect={onCourierSelected}
          />
        </div>

        <div className="section">
          <h5>
            <span>Assigned Couriers ({partition.couriers?.length || 0})</span>
            <Tooltip title="Hover to Courier's name will show their preferred areas">
              <span className="tooltip icon">
                <FaExclamationCircle size={16} color="grey" />
              </span>
            </Tooltip>
          </h5>
          <div className="couriers-container">
            {partition.couriers?.length > 0 ? (
              <>
                <Grid container spacing={1}>
                  {assignedCouriers.map(courier => (
                    <Grid
                      item
                      key={`assigned-courier-${courier?.id || uniqueId()}`}
                    >
                      <CourierChip
                        courier={courier}
                        onRemoveFromPartition={removeCourierFromPartition}
                        onReassignToActivePartition={
                          addCourierToActivePartition
                        }
                        onCourierHoverChange={props.onCourierHoverChange}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Divider />
                <Grid container spacing={1}>
                  <Grid
                    item
                    className="shortcut"
                    onMouseEnter={() =>
                      props.onCourierHoverChange(
                        assignedCouriers,
                        AreaRating.LIKE
                      )
                    }
                    onMouseLeave={() => props.onCourierHoverChange(undefined)}
                  >
                    <Tooltip title="This partition's couriers LIKED areas">
                      <Chip label="Liked Area" variant="outlined" />
                    </Tooltip>
                  </Grid>
                  <Grid
                    item
                    className="shortcut"
                    onMouseEnter={() =>
                      props.onCourierHoverChange(
                        assignedCouriers,
                        AreaRating.DISLIKE
                      )
                    }
                    onMouseLeave={() => props.onCourierHoverChange(undefined)}
                  >
                    <Tooltip title="This partition's couriers DISLIKED areas">
                      <Chip label="Disliked Area" variant="outlined" />
                    </Tooltip>
                  </Grid>
                </Grid>
              </>
            ) : (
              <p>No couriers assigned on this partition yet.</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
